import type {
  ExtendRequest,
  ImageToVideoRequest,
  InterpolateRequest,
  RestyleRequest,
  TextToVideoRequest,
  VideoLabOperationRequest,
  VideoModelTier,
} from '../../../domain';

/**
 * Common motion controls shared by every panel — the tier/model selection plus
 * the cost-bearing knobs (duration / aspect / resolution / audio). Phase 1
 * defaults (A-B8): 4s · 16:9 · 720p · audio off · Value tier.
 */
export interface VideoControls {
  modelTier: VideoModelTier;
  /** Advanced override — a specific model id within the tier. Wins over tier. */
  model?: string;
  durationSeconds: number;
  aspectRatio: string;
  resolution: string;
  generateAudio: boolean;
  seed?: number;
}

export const DEFAULT_CONTROLS: VideoControls = {
  modelTier: 'value',
  durationSeconds: 4,
  aspectRatio: '16:9',
  resolution: '720p',
  generateAudio: false,
};

/** Map the shared controls onto the request base. Omits empty optionals so the
 * BE's tier/default resolution stays authoritative when a field is unset. */
function applyControls(c: VideoControls): VideoLabOperationRequest {
  const base: VideoLabOperationRequest = {
    modelTier: c.modelTier,
    durationSeconds: c.durationSeconds,
    aspectRatio: c.aspectRatio,
    resolution: c.resolution,
    generateAudio: c.generateAudio,
  };
  if (c.model) base.model = c.model;
  if (c.seed !== undefined) base.seed = c.seed;
  return base;
}

export function buildTextToVideo(prompt: string, c: VideoControls): TextToVideoRequest {
  return { ...applyControls(c), prompt: prompt.trim() };
}

export interface ImageToVideoInput {
  sourceImageId?: string;
  sourceImageUrl?: string;
  sourceImageBase64?: string;
  prompt?: string;
}

export function buildImageToVideo(input: ImageToVideoInput, c: VideoControls): ImageToVideoRequest {
  const req: ImageToVideoRequest = { ...applyControls(c) };
  if (input.sourceImageId) req.sourceImageId = input.sourceImageId;
  if (input.sourceImageUrl) req.sourceImageUrl = input.sourceImageUrl;
  if (input.sourceImageBase64) req.sourceImageBase64 = input.sourceImageBase64;
  if (input.prompt?.trim()) req.prompt = input.prompt.trim();
  return req;
}

export interface InterpolateInput {
  firstFrameImageId?: string;
  firstFrameImageUrl?: string;
  lastFrameImageId?: string;
  lastFrameImageUrl?: string;
  prompt?: string;
}

export function buildInterpolate(input: InterpolateInput, c: VideoControls): InterpolateRequest {
  const req: InterpolateRequest = { ...applyControls(c) };
  if (input.firstFrameImageId) req.firstFrameImageId = input.firstFrameImageId;
  if (input.firstFrameImageUrl) req.firstFrameImageUrl = input.firstFrameImageUrl;
  if (input.lastFrameImageId) req.lastFrameImageId = input.lastFrameImageId;
  if (input.lastFrameImageUrl) req.lastFrameImageUrl = input.lastFrameImageUrl;
  if (input.prompt?.trim()) req.prompt = input.prompt.trim();
  return req;
}

export function buildExtend(sourceVideoId: string, prompt: string, c: VideoControls): ExtendRequest {
  const req: ExtendRequest = { ...applyControls(c), sourceVideoId };
  if (prompt.trim()) req.prompt = prompt.trim();
  return req;
}

export function buildRestyle(sourceVideoId: string, prompt: string, c: VideoControls): RestyleRequest {
  return { ...applyControls(c), sourceVideoId, prompt: prompt.trim() };
}

/** Pre-submit cost estimate: duration × the selected model's per-second price.
 * Returns null when price is unknown (catalog still loading). */
export function estimateCost(durationSeconds: number, pricePerSecond?: number): number | null {
  if (pricePerSecond === undefined || pricePerSecond === null) return null;
  return Math.round(durationSeconds * pricePerSecond * 100) / 100;
}
